import React, { useCallback, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, withRouter } from 'react-router-dom';
import { Grid } from '@mui/material';
import DocumentDetailCard from './DocumentDetailCard';
import NotFound from 'components/common/NotFound';
import { getDocumentRightsByStateKey, getRightToViewInsuranceByDocumentType } from 'constants/common';
import { useQueryWithErrorHandling } from 'customHooks/errorHandling';
import { LEASING_OFFER } from '../queries';
import { DOCUMENT_DETAILS_CHANGED, LEASING_OFFER_CHANGED } from '../subscriptions';
import { useGlobalSubscription } from 'providers/GlobalCacheManager';
import { ApplicationFeaturesContext } from 'providers/ApplicationFeaturesContext';
import GeneralInsuranceDataContainer from './GeneralInsuranceDataContainer';
import { useMemo } from 'react';
import WrappedFakeText from 'components/common/WrappedFakeText';

const splitAssets = documentDetails => {
  let details = [];
  documentDetails.forEach(detail => {
    const { assetInformation } = detail;
    if (!assetInformation) {
      details.push({ ...detail, assetInformationId: null });
      return;
    }

    const assetsWithChassisNumber = assetInformation.filter(z => !!z.chassisNumber);
    if (assetsWithChassisNumber.length === 0) {
      details.push({ ...detail, assetInformationId: null });
    } else {
      const { insuranceSimulations } = detail;
      const assets = assetsWithChassisNumber.map(z => {
        const detailClone = JSON.parse(JSON.stringify(detail));
        detailClone.quantity = 1;
        detailClone.chassisNumber = z.chassisNumber;
        detailClone.assetInformationId = z.id;
        detailClone.insuranceSimulations = insuranceSimulations.filter(i => i.assetInformationId === z.id);
        return detailClone;
      });
      details.push(...assets);

      const remainingQuantity = detail.quantity - assetsWithChassisNumber.length;
      if (remainingQuantity > 0) {
        const bulk = {
          ...detail,
          quantity: remainingQuantity,
          assetInformationId: null,
          insuranceSimulations: insuranceSimulations.filter(i => i.assetInformationId === null)
        };
        details.push(bulk);
      }
    }
  });
  return details;
};

const InsuranceDocumentDetailsContainer = props => {
  const { offerId, offerSiteId, expandedDocumentDetail, hasRightToViewInsuranceByDocumentType, offerInsuranceRead } = props;
  const history = useHistory();

  const { webInsuranceIntegration } = useContext(ApplicationFeaturesContext);

  const [hasErrors, setHasErrors] = useState(false);

  const canReadInsurance = useMemo(
    () => offerInsuranceRead && webInsuranceIntegration && hasRightToViewInsuranceByDocumentType,
    [hasRightToViewInsuranceByDocumentType, offerInsuranceRead, webInsuranceIntegration]
  );

  const { data: leasingOfferData, refetch: refetchLeasingOfferData } = useQueryWithErrorHandling(LEASING_OFFER, {
    skip: !canReadInsurance,
    variables: { offerId, offerSiteId },
    cacheEviction: true,
    context: {
      headers: { offerId, offerSiteId }
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    onError: () => setHasErrors(true)
  });

  const documentDetails = useMemo(() => leasingOfferData?.leasingOffer?.leasingSimulation?.documentDetails, [leasingOfferData]);

  useGlobalSubscription(DOCUMENT_DETAILS_CHANGED, { variables: { offerId, offerSiteId } });
  useGlobalSubscription(LEASING_OFFER_CHANGED, { variables: { documentId: offerId, siteId: offerSiteId } });

  const handleExpand = useCallback(
    (expanded, documentDetail) => {
      let route = `/offers/${offerId}/${offerSiteId}/insurance`;
      if (!expanded) {
        history.push(route);
        return;
      }

      const { documentDetailId, siteId, assetInformationId } = documentDetail;
      route = `${route}/${documentDetailId}/${siteId}`;
      if (assetInformationId) route = `${route}/${assetInformationId}`;
      history.push(route);
    },
    [offerId, offerSiteId, history]
  );

  if (!canReadInsurance || hasErrors) return <NotFound />;
  if (!documentDetails) return <WrappedFakeText lines={5} />;

  const insuranceDetails = splitAssets(documentDetails);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <GeneralInsuranceDataContainer leasingOfferData={leasingOfferData} refetch={refetchLeasingOfferData} />
      </Grid>
      <Grid item container xs={12}>
        {insuranceDetails
          ?.filter(
            documentDetail =>
              !expandedDocumentDetail ||
              (documentDetail.documentDetailId === expandedDocumentDetail.documentDetailId &&
                documentDetail.siteId === expandedDocumentDetail.siteId &&
                documentDetail.assetInformationId === expandedDocumentDetail.assetInformationId)
          )
          .map(documentDetail => (
            <Grid item xs={12} key={`${documentDetail.documentDetailId}-${documentDetail.siteId}`}>
              <DocumentDetailCard documentDetail={documentDetail} expanded={!!expandedDocumentDetail} onExpand={handleExpand} />
            </Grid>
          ))}
      </Grid>
    </Grid>
  );
};

InsuranceDocumentDetailsContainer.propTypes = {
  offerId: PropTypes.number.isRequired,
  offerSiteId: PropTypes.number.isRequired,
  expandedDocumentDetail: PropTypes.object,
  hasRightToViewInsuranceByDocumentType: PropTypes.bool.isRequired,
  offerInsuranceRead: PropTypes.bool.isRequired
};

function mapStateToProps(state) {
  const { documentId, siteId, documentTypeId, stateId } = state.currentOffer.header;
  const rightsStateKey = getDocumentRightsByStateKey(stateId, documentTypeId);
  const offerRights = state.documentRights[rightsStateKey];
  const offerInsuranceRead = Boolean(offerRights?.offerInsuranceRead);
  const hasRightToViewInsuranceByDocumentType = getRightToViewInsuranceByDocumentType(documentTypeId);

  return {
    offerInsuranceRead,
    offerId: documentId,
    offerSiteId: siteId,
    hasRightToViewInsuranceByDocumentType
  };
}

export default compose(withRouter, connect(mapStateToProps))(InsuranceDocumentDetailsContainer);
